import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { api } from './api';
import { Telemetry } from './telemetry';
import { createLogger } from './debug';
import type { Project } from '@/types/api';

const log = createLogger('project');

interface ProjectContextValue {
  projects: Project[];
  activeProject: Project | null;
  setActiveProject: (project: Project) => void;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  conflictCount: number;
  refreshConflictCount: () => Promise<void>;
  needsProjectSelection: boolean;
}

const ProjectContext = createContext<ProjectContextValue>({
  projects: [],
  activeProject: null,
  setActiveProject: () => {},
  loading: true,
  error: null,
  reload: async () => {},
  conflictCount: 0,
  refreshConflictCount: async () => {},
  needsProjectSelection: false,
});

export function ProjectProvider({ children }: { children: ReactNode }) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [activeProject, setActiveProjectState] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [conflictCount, setConflictCount] = useState(0);

  const refreshConflictCount = useCallback(async () => {
    const proj = activeProject;
    if (!proj) { setConflictCount(0); return; }
    try {
      const [files, dupResult] = await Promise.all([
        api.projects.unclassified(proj.id),
        api.projects.duplicateCount(proj.id),
      ]);
      setConflictCount(files.filter((f) => !f.resolved).length + dupResult.count);
    } catch {
      // ignore
    }
  }, [activeProject]);

  const load = async () => {
    setError(null);
    try {
      const list = await api.projects.list();
      setProjects(list);

      // Restore active project from localStorage
      const savedId = localStorage.getItem('chrona_active_project');
      const saved = list.find((p) => p.id === savedId);
      if (saved) {
        setActiveProjectState(saved);
      } else if (list.length === 1) {
        // Single project: auto-select
        setActiveProjectState(list[0]);
        localStorage.setItem('chrona_active_project', list[0].id);
      } else {
        // Saved project was deleted or no saved preference — clear stale state
        setActiveProjectState(null);
        localStorage.removeItem('chrona_active_project');
      }
    } catch {
      setError('Could not connect to the backend. Is the server running on port 8000?');
    } finally {
      setLoading(false);
    }
  };

  // On mount: try to load projects; if backend isn't ready, retry silently
  useEffect(() => {
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const tryLoad = async (attempt: number) => {
      log.log(`tryLoad attempt=${attempt}`);
      try {
        const list = await log.time('projects.list', () => api.projects.list());
        if (cancelled) return;
        log.log(`got ${list.length} project(s)`, list.map(p => ({ id: p.id, name: p.name })));
        setProjects(list);
        setError(null);

        const savedId = localStorage.getItem('chrona_active_project');
        const saved = list.find((p) => p.id === savedId);
        if (saved) {
          log.log('restored active project from localStorage:', saved.id, saved.name);
          setActiveProjectState(saved);
        } else if (list.length === 1) {
          log.log('auto-selecting sole project:', list[0].id, list[0].name);
          setActiveProjectState(list[0]);
          localStorage.setItem('chrona_active_project', list[0].id);
        } else {
          log.log('no saved project, needsProjectSelection=true');
          setActiveProjectState(null);
          localStorage.removeItem('chrona_active_project');
        }
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        log.warn(`tryLoad attempt=${attempt} failed`, err);
        if (attempt < 3) {
          // First few attempts: stay on loading spinner and retry
          retryTimer = setTimeout(() => tryLoad(attempt + 1), 2000);
        } else {
          // Give up waiting — show FRE (or error state)
          setError('Could not connect to the backend. Is the server running on port 8000?');
          setLoading(false);
        }
      }
    };

    tryLoad(0);

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, []);

  // Refresh conflict count whenever active project changes
  useEffect(() => {
    refreshConflictCount();
  }, [refreshConflictCount]);

  const setActiveProject = (project: Project) => {
    setActiveProjectState(project);
    localStorage.setItem('chrona_active_project', project.id);
    Telemetry.projectSwitched({ project_id: project.id, project_name: project.name });
  };

  const needsProjectSelection = !loading && projects.length > 1 && activeProject === null;

  return (
    <ProjectContext.Provider
      value={{ projects, activeProject, setActiveProject, loading, error, reload: load, conflictCount, refreshConflictCount, needsProjectSelection }}
    >
      {children}
    </ProjectContext.Provider>
  );
}

export function useProject() {
  return useContext(ProjectContext);
}
